import { type SearchUrl } from '@vakantiesnl/services/src/hooks/useSearchUrlBuilder';
import { type GTM, type Search, type BreadCrumbItem } from '@vakantiesnl/types';
import { isUndefined } from 'lodash';

import { basicGTMV2Event } from './basicGTMV2Event';
import { accoEventValueConstructor, mapOffer, toSnakeCase, type Geo } from './util';
import { type FlightData, type RoomData } from '../context/useAccommodationContext';
import { isPackageOffer } from '../util';

export interface availabilityCheckData {
	product_status: string;
	request_price: number;
	available_price: number;
	flight_status: boolean;
	hotel_status: boolean;
	tour_operator?: string;
	flight_data?: FlightData;
	room_data?: RoomData;
	acco_type?: string;
	giata_id?: number;
	helios_id: number;
	geo?: Geo;
	label?: string;
	uuid?: string;
}

/** Track if the user selected an offer from the price bars or if the offer was automatically preselected. */
export const pricebarSelect = (
	offer: Search.OfferItem | Search.AccommodationOnlyOffer,
	acco: Search.FullAccommodationItem,
	party: Search.Party,
	initialLoad?: boolean,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'product',
		organism: 'accommodation',
		molecule: 'tripoffers',
		atom: 'pricebar',
		action: initialLoad ? 'preselect' : 'select',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			departure_date: offer.departureDate,
			id: offer.id,
			type: acco.type,
			giata_id: acco.giataId?.toString(),
			geo_path: `${acco.country}/${acco.region}/${acco.city}`,
			product_code: offer.productCode.toString(),
			booking_code: isPackageOffer(offer) ? offer.bookingCode.toString() : null,
			tour_operator: isPackageOffer(offer) ? offer.tourOperator.toString() : null,
			name: acco.name,
			travellers: {
				pax: party.total,
				adult: party.adults,
				child: party.children,
				baby: party.babies,
			},
			price: {
				currency: 'EUR',
				price_total: (offer.priceTotal / 100).toFixed(2),
				price_per_adult: (offer.pricePerAdult / 100).toFixed(2),
				price_per_child: (offer.pricePerChild / 100).toFixed(2),
				price_per_baby: (offer.pricePerBaby / 100).toFixed(2),
			},
			duration_days: offer.durationDays,
			duration_nights: offer.durationNights,
			extras: {
				has_transfer: isPackageOffer(offer) ? offer.rooms.some((room) => room.hasTransfer) : null,
			},
			mealplan: isPackageOffer(offer) ? offer.rooms[0].board.code : null,
			rooms: isPackageOffer(offer) ? offer.rooms.map((room) => room.codes) : null,
			inbound: isPackageOffer(offer) ? toSnakeCase(offer.inbound) : null,
			outbound: isPackageOffer(offer) ? toSnakeCase(offer.outbound) : null,
			geo: {
				country_name: acco.country,
				region_name: acco.region,
				city_name: acco.city,
			},
		},
	});

export const noOffersLinkClick = (
	noOffersLink: SearchUrl,
	party: Search.PartyComposition[],
	departureDate: string[] | null,
	mealplans: string[] | undefined,
	durationRange: number[],
	airports: string[] | undefined,
	heliosId: number,
): GTM.GTMEventV2 => {
	/** Get the number of selected filters */
	const getNrOfFilters = (): number => {
		let count = 0;
		if (departureDate) count++;
		if (!!mealplans?.length) count++;
		if (!!durationRange?.length) count++;
		if (!!airports?.length) count++;
		return count;
	};

	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'tripoffers',
		atom: 'no_offers_link',
		action: 'select',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			no_offers_link: `${noOffersLink.as.pathname}?${noOffersLink.as.query}`,
			no_of_filters: getNrOfFilters(),
			filters: {
				departureDate: departureDate ? departureDate : null,
				durations: durationRange,
				partyComposition: party,
				mealplans,
				departureAirport: airports,
			},
		},
	});
};

type FlightFiltersGtmProps = {
	noOfFilters: number;
	actionLabel: string;
	outboundFlight?: string;
	inboundFlight?: string;
	includesTransfer?: boolean;
	heliosId: number;
	initialLoad?: boolean;
};

export const flightFiltersGTM = ({
	outboundFlight,
	inboundFlight,
	includesTransfer,
	heliosId,
	noOfFilters = 0,
	initialLoad,
	actionLabel,
}: FlightFiltersGtmProps): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'flights',
		atom: 'flight_filter_menu',
		action: initialLoad ? 'preselect' : 'select',
		label: actionLabel,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			no_of_filters: noOfFilters,
			filters: {
				outbound_flight_period: outboundFlight ?? null,
				inbound_flight_period: inboundFlight ?? null,
				includes_transfer: includesTransfer,
			},
		},
	});

export const availabilityCheck = ({
	product_status,
	request_price,
	available_price,
	flight_status,
	hotel_status,
	tour_operator,
	giata_id,
	flight_data,
	room_data,
	acco_type,
	helios_id,
	geo,
	label,
}: availabilityCheckData): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'load',
		organism: 'availability_check_modal',
		molecule: 'availability_check',
		atom: null,
		action: 'check',
		label: label ?? product_status,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: helios_id.toString(),
		entity_level: 'accommodation',
		value: {
			type: acco_type,
			request_price: request_price / 100,
			available_price: available_price / 100,
			status: {
				is_hotel_available: hotel_status,
				is_flight_available: flight_status,
				status: product_status,
			},
			tour_operator: tour_operator,
			selected_flight: flight_data && !flight_data.loading ? flight_data.selected : null,
			selected_room: room_data && !room_data.loading ? room_data.selected : null,
			giata_id: giata_id,
			geo: geo,
		},
	});
};

export const flightSelect = (
	acco: Search.FullAccommodationItem,
	offer: Search.OfferItem,
	position: number,
	accoType?: string,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'offer',
		organism: 'accommodation',
		molecule: 'flightselector',
		atom: 'flight',
		action: 'select',
		label: offer.id,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			type: accoType,
			position,
			geo: {
				country_name: acco.country,
				region_name: acco.region,
				city_name: acco.city,
			},
			...toSnakeCase(offer),
		},
	});

type FlightPaginationProps = {
	resultsShown: number;
	resultsTotal: number;
	actionLabel: string;
	heliosId: number;
};
export const flightPagination = ({
	resultsShown,
	resultsTotal,
	heliosId,
	actionLabel,
}: FlightPaginationProps): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'flights',
		atom: 'flight_filter_menu',
		action: 'click',
		label: actionLabel,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			no_of_results_total: resultsTotal,
			no_of_results_shown: resultsShown,
		},
	});
export const mealPlanSelect = (
	acco: Search.FullAccommodationItem,
	offer: Search.OfferItem,
	label: string,
	priceDiff?: number,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'meal_plan_selector',
		atom: 'meal_plan',
		action: 'select',
		label: label,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			rooms: offer.rooms.map((room) => ({ ...toSnakeCase(room) })),
			price_difference: priceDiff ? priceDiff / 100 : undefined,
		},
	});

export const roomSelect = (
	acco: Search.FullAccommodationItem,
	offer: Search.OfferItem | Search.AccommodationOnlyOffer,
	label: string,
	priceDiff?: number,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'room_selector',
		atom: 'room',
		action: 'select',
		label: label,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			rooms: isPackageOffer(offer)
				? offer.rooms.map((room) => ({ ...toSnakeCase(room) }))
				: offer.productInfo?.properties.map((property) => ({ ...toSnakeCase(property) })),
			price_difference: priceDiff ? priceDiff / 100 : undefined,
		},
	});

export const accommodationPageView = (
	acco: Search.FullAccommodationItem,
	offer?: string,
	geo?: Geo,
	adlinkCount?: number,
	hasZooverDeal?: boolean,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'product',
		organism: null,
		molecule: null,
		atom: null,
		action: 'page_view',
		label: offer ? offer : null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco, geo, undefined, adlinkCount, hasZooverDeal),
	});

export const viewOffersClick = (
	acco: Search.FullAccommodationItem,
	microCopyLabel: string,
	adlinkCount?: number,
	isLastImageCtaClicked?: boolean,
	isPriceCardBookBtnClicked?: boolean,
	isImageModalHeader?: boolean,
	isNonBookableProvidersBtnClicked?: boolean,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: isImageModalHeader ? 'photo_overview_modal' : 'price_card',
		atom: isNonBookableProvidersBtnClicked
			? 'adlink_modal_button'
			: isPriceCardBookBtnClicked || isImageModalHeader
				? 'tripoffers_scroll_button'
				: 'viewofferscta',
		action: 'click',
		label: microCopyLabel,
		page_type: 'accommodation',
		sub_page: isLastImageCtaClicked ? 'last_image_CTA' : 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco, undefined, undefined, adlinkCount),
	});

export const bookNowRoomType = (acco: Search.FullAccommodationItem): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'room_type_selection',
		atom: 'book_now_cta',
		action: 'click',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco),
	});

export const bookNowStickyOfferSummary = (acco: Search.FullAccommodationItem, label: string): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'offer_summary',
		atom: 'book_now_cta',
		action: 'click',
		label,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco),
	});

export const reviewsCTAClick = (
	acco: Search.FullAccommodationItem,
	type: 'action' | 'load',
	label: string,
	atom: string,
	action: GTM.GTMAction,
	adlinkCount?: number,
	isAccoBannerBookBtn?: boolean,
	isAccoBannerProvidersBtn?: boolean,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: type,
		organism: 'accommodation',
		molecule: 'reviews',
		atom: isAccoBannerBookBtn ? 'book_now_cta' : isAccoBannerProvidersBtn ? 'adlink_modal_button' : atom,
		action: isAccoBannerProvidersBtn ? 'open' : action,
		label: label,
		page_type: 'accommodation',
		sub_page: 'reviews',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco, undefined, undefined, adlinkCount),
	});

export const imageModalAdlinksCTAClick = (
	acco: Search.FullAccommodationItem,
	label: string,
	adlinkCount?: number,
	isLastImageCtaClicked?: boolean,
	isImageModalHeader?: boolean,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: isImageModalHeader ? 'photo_overview_modal' : 'photos_modal',
		atom: isImageModalHeader ? 'adlink_modal_button' : 'partner_modal',
		action: 'open',
		label,
		page_type: 'accommodation',
		sub_page: isLastImageCtaClicked ? 'last_image_CTA' : 'photos_modal',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco, undefined, undefined, adlinkCount),
	});

export const imageView = (
	acco: Search.FullAccommodationItem,
	offer?: Search.OfferItem | Search.AccommodationOnlyOffer | null,
	geo?: Geo,
	photoPosition?: number,
	photoId?: string,
	adlinkCount?: number,
): GTM.GTMEventV2 => {
	let mappedOffer: GTM.GTMEventV2['value'] = mapOffer(offer);

	if (offer) {
		const convertedPrices: GTM.GTMEventV2['value'] = {
			currency: 'EUR',
			price_total: (offer.priceTotal / 100).toFixed(2),
			price_per_adult: (offer.pricePerAdult / 100).toFixed(2),
			price_per_child: (offer.pricePerChild / 100).toFixed(2),
			price_per_baby: (offer.pricePerBaby / 100).toFixed(2),
		};
		mappedOffer = { ...mapOffer(offer), price: convertedPrices };
	}

	return basicGTMV2Event({
		type: 'view',
		organism: 'accommodation',
		molecule: 'photos_modal',
		atom: 'photo',
		action: 'view',
		label: null,
		page_type: 'accommodation',
		sub_page: 'photos_modal',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			...mappedOffer,
			...accoEventValueConstructor(acco, geo, undefined, adlinkCount),
			photos_position: photoPosition,
			photos_id: photoId,
		},
	});
};

export const accommodationFilterChanges = (
	party: Search.PartyComposition[],
	departureDate: string[] | null,
	mealplans: string[] | undefined,
	durationRange: number[],
	airports: string[] | undefined,
	acco: Search.FullAccommodationItem,
	initialLoad?: boolean,
): GTM.GTMEventV2 => {
	/** Get the number of selected filters */
	const getNrOfFilters = (): number => {
		let count = 0;
		if (departureDate) count++;
		if (!!mealplans?.length) count++;
		if (!!durationRange?.length) count++;
		if (!!airports?.length) count++;
		return count;
	};

	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'tripoffers',
		atom: 'offers_filter_menu',
		action: initialLoad ? 'preselect' : 'select',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			no_of_filters: getNrOfFilters(),
			departureDate: departureDate ? departureDate : null,
			durations: durationRange,
			partyComposition: party,
			mealplans,
			departureAirport: airports,
		},
	});
};

export const breadCrumbClick = (
	destinationUrl: string,
	heliosId?: number,
	destinationType?: BreadCrumbItem['destinationType'],
	isAccoHeader?: boolean,
	atomType?: string,
	label?: string,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: isAccoHeader ? 'acco_header' : 'breadcrumb',
		atom: atomType ? atomType : 'breadcrumb_link',
		action: 'click',
		label: label ? label : null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: heliosId?.toString(),
		entity_level: 'accommodation',
		value: {
			breadcrumb_url: destinationUrl,
			section: !isUndefined(destinationType) ? destinationType.toLowerCase() : 'accommodation_type', // not working for acco type
		},
	});

export const onRoomTypeOpen = (acco: Search.FullAccommodationItem, geo?: Geo): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'room_selector',
		atom: 'tooltip',
		action: 'open',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco, geo),
	});

export const onMealplanModalOpen = (acco: Search.FullAccommodationItem, geo?: Geo): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'mealplan_selector',
		atom: 'tooltip',
		action: 'open',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco, geo),
	});

export const onPageNavBarClick = (
	acco: Search.FullAccommodationItem,
	menuItem: string,
	label: string,
	geo?: Geo,
	adlinkCount?: number,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation_navigation',
		molecule: null,
		atom: 'link',
		action: 'click',
		label: label,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			accommodation_navigation_section: menuItem,
			...accoEventValueConstructor(acco, geo, undefined, adlinkCount),
		},
	});

/** Track which offers does the user see in the acco page price bars - upon both initial load and user interaction. */
export const tripOffersLoad = (
	acco: Search.FullAccommodationItem,
	offers: Search.OfferItem[] | Search.AccommodationOnlyOffer[],
	type: 'load' | 'action',
	action: 'view' | 'click',
	label: string | null,
): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: type,
		organism: 'accommodation',
		molecule: 'tripoffers',
		atom: 'pricebar',
		action: action,
		label: label,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			no_of_offers: offers.length,
			offers: offers.map((offer) => ({
				id: offer.id,
				departure_date: offer.departureDate,
				product_code: offer.productCode,
				booking_code: isPackageOffer(offer) ? offer.bookingCode : null,
				tour_operator: isPackageOffer(offer) ? offer.tourOperator : null,
				price: {
					currency: 'EUR',
					price_total: offer.priceTotal,
					price_per_adult: offer.pricePerAdult,
					price_per_child: offer.pricePerChild,
					price_per_baby: offer.pricePerBaby,
				},
				duration_days: offer.durationDays,
				duration_nights: offer.durationNights,
				extras: {
					has_transfer: isPackageOffer(offer) ? offer.rooms.some((room) => room.hasTransfer) : null,
					has_luggage: isPackageOffer(offer) ? offer.hasLuggageIncluded : null,
				},
			})),
		},
	});
};

export const onShareAccoPageButtonClick = (
	acco: Search.FullAccommodationItem,
	accoLink: string,
	isAccoHeader?: boolean,
): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: isAccoHeader ? 'acco_header' : 'acco_type',
		atom: isAccoHeader ? 'share' : 'share_accommodation',
		action: 'open',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			acco_link: accoLink,
		},
	});
};

export const onCopyLinkButtonClick = (acco: Search.FullAccommodationItem, accoLink: string): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'share_modal',
		atom: 'copy_link',
		action: 'click',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			acco_link: accoLink,
		},
	});
};

export const onShareViaSocialButtonClick = (
	acco: Search.FullAccommodationItem,
	accoLink: string,
	label: string,
): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'share_modal',
		atom: 'social_sharing',
		action: 'click',
		label: label,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			acco_link: accoLink,
		},
	});
};

export const offerOverviewTabSwitch = (
	tabLabel: string | null,
	acco: Search.FullAccommodationItem,
	atom: string,
): GTM.GTMEventV2 => ({
	...accommodationPageView(acco),
	type: 'action',
	organism: 'accommodation',
	molecule: 'offer_summary',
	atom: atom,
	action: 'click',
	label: tabLabel,
});

export const reviewAccommodationClick = (
	acco: Search.FullAccommodationItem,
	isImageModalOpen: boolean,
	adlinkCount?: number,
): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'acco_header',
		atom: 'review_accommodation',
		action: 'click',
		label: null,
		page_type: 'accommodation',
		sub_page: isImageModalOpen ? 'photos_modal' : 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: {
			...accoEventValueConstructor(acco, undefined, undefined, adlinkCount),
		},
	});
};

export const toggleImageModalView = (acco: Search.FullAccommodationItem): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'photos_modal',
		atom: 'toggle_view_button',
		action: 'click',
		label: null,
		page_type: 'accommodation',
		sub_page: 'photos_modal',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco),
	});

export const trackReviewImageBtn = (acco: Search.FullAccommodationItem, microCopyLabel: string): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'photos_modal',
		atom: 're_view_photos',
		action: 'click',
		label: microCopyLabel,
		page_type: 'accommodation',
		sub_page: 'last_image_CTA',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco),
	});

export const trackClickShowMoreDescription = (
	acco: Search.FullAccommodationItem,
	microCopyLabel: string,
): GTM.GTMEventV2 =>
	basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'description',
		atom: 'description_show_more_button',
		action: 'click',
		label: microCopyLabel,
		page_type: 'accommodation',
		sub_page: null,
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco),
	});

export const trackFacilityGroupToggle = (
	acco: Search.FullAccommodationItem,
	open: boolean,
	groupTitle: string,
): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation_page',
		molecule: 'facilities',
		atom: 'facilities_accordion',
		action: open ? 'open' : 'close',
		label: groupTitle,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco),
	});
};

export const trackImageModalClick = (acco: Search.FullAccommodationItem, isImageModalOpen: boolean): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'photos_modal',
		atom: 'photo',
		action: !isImageModalOpen ? 'open' : 'close',
		label: null,
		page_type: 'accommodation',
		sub_page: 'overview',
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco),
	});
};

export const trackWriteReviewClick = (acco: Search.FullAccommodationItem, label: string): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'reviews',
		atom: 'review_accommodation',
		action: 'click',
		label: label,
		page_type: 'accommodation',
		sub_page: null,
		entity_id: acco.heliosId.toString(),
		entity_level: 'accommodation',
		value: accoEventValueConstructor(acco),
	});
};

export const trackAccommodationTopBannerClick = (entity_id: string): GTM.GTMEventV2 => {
	return basicGTMV2Event({
		type: 'action',
		organism: 'accommodation',
		molecule: 'top_banner',
		atom: 'top_banner_link',
		action: 'click',
		label: null,
		page_type: 'accommodation',
		sub_page: null,
		entity_id,
		entity_level: 'accommodation',
		value: {},
	});
};
